import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../../../auth";
import { Candidate } from "../../../interfaces/candidate";
import { getCandidateByEmail } from "../../../services/candidateService";
import { Order } from "../../../interfaces/order";
import { getOrderByID } from "../../../services/orderService";
import { useLocation, useNavigate } from "react-router-dom";
import { OrderForm } from "../../../interfaces/orderForm";
import { getOrderFormByID } from "../../../services/orderFormService";
import PersonalInformationForm, {
  PersonalInformation,
} from "../../../components/form-components/PersonalInformationForm";
import ConvictedOfCrimeForm, {
  ConvictedOfCrime,
} from "../../../components/form-components/ConvictedOfCrimeForm";
import LegalNamesForm, {
  LegalName,
} from "../../../components/form-components/LegalNamesForm";
import AddressForm, {
  Address,
} from "../../../components/form-components/AddressForm";
import AuthorizationForm, {
  Authorization,
} from "../../../components/form-components/AuthorizationForm";
import {
  formatDate,
  isValidBirthday,
  isValidCounty,
  isValidDate,
  isValidEmail,
  isValidPhone,
  isValidSIN,
  isValidSSN,
  isValidZip,
  validateAddressHistory,
} from "../../../services/helpers";
import { saveCriminalRecordsAuthForm } from "../../../services/orderFormService";
import { getCriminalOrderByOrderID } from "../../../services/criminalOrderService";
import { CriminalOrder } from "../../../interfaces/criminalOrder";
import { uploadSpecialReleaseForm } from "../../../services/specialReleaseFormService";
import { getSimpleClient } from "../../../services/clientService";
import { SimpleClient } from "../../../interfaces/client";
import { OrderService } from "../../../interfaces/orderService";
import { getOrderServicesByOrderID } from "../../../services/orderServiceService";
import EducationForm, {
  Education,
} from "../../../components/form-components/EducationForm";
import {
  deleteDraft,
  loadCriminalAuthDraft,
  saveDraftCriminalAuth,
} from "../../../services/draftService";

export interface CriminalAuthDraft {
  personalInformation: PersonalInformation;
  convictedOfCrimeList: ConvictedOfCrime[];
  convictedOfCrimeSelection: boolean | null;
  legalNamesList: LegalName[];
  additionalNameSelection: boolean | null;
  residentialAddressList: Address[];
  employmentAddressList: Address[];
  authorization: Authorization;
  educationList: Education[];
  unemployed: boolean;
}

const CriminalRecordsAuthForm = () => {
  const navigate = useNavigate();
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  const { decodeToken } = useAuth();
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [order, setOrder] = useState<Order | null>(null);
  const [criminalOrder, setCriminalOrder] = useState<CriminalOrder | null>(
    null,
  );
  const [orderForm, setOrderForm] = useState<OrderForm | null>(null);
  const [client, setClient] = useState<SimpleClient | null>(null);
  const location = useLocation();
  const { orderID, orderFormID, anyCompletedForms } = location.state || {};
  const [fileUploaded, setFileUploaded] = useState(false);
  const [unemployed, setUnemployed] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [startDate, setStartDate] = useState<Date>();
  const [convictedOfCrimeList, setConvictedOfCrimeList] = useState<
    ConvictedOfCrime[]
  >([]);
  const [convictedOfCrimeSelection, setConvictedOfCrimeSelection] = useState<
    boolean | null
  >(null);
  const [legalNamesList, setLegalNamesList] = useState<LegalName[]>([]);
  const [additionalNameSelection, setAdditionalNameSelection] = useState<
    boolean | null
  >(null);
  const [residentialAddressList, setResidentialAddressList] = useState<
    Address[]
  >([
    {
      address: "",
      zipCode: "",
      city: "",
      state: "",
      country: "",
      county: "",
      fromDate: new Date(),
      toDate: new Date(),
    },
  ]);
  const [employmentAddressList, setEmploymentAddressList] = useState<Address[]>(
    [
      {
        address: "",
        zipCode: "",
        city: "",
        state: "",
        country: "",
        county: "",
        fromDate: new Date(),
        toDate: new Date(),
      },
    ],
  );
  const [personalInformation, setPersonalInformation] =
    useState<PersonalInformation>({
      firstName: "",
      lastName: "",
      middleName: "",
      email: candidate?.candidateEmail || "",
      dateOfBirth: new Date(),
      phone: "",
      race: "",
      sex: "",
      socialSecurityNumber: "",
      canadianSocialSecurityNumber: "",
    });
  const [authorization, setAuthorization] = useState<Authorization>({
    checked: false,
    dated: new Date(),
    aCity: "",
    aState: "",
    fullName: "",
  });
  const [educationList, setEducationList] = useState<Education[]>([]);
  const [hasEmploymentService, setHasEmploymentService] = useState(false);
  const [hasOneEducationService, setHasOneEducationService] = useState(false);
  const [hasMultipleEducationService, setHasMultipleEducationService] =
    useState(false);
  const hasRun = useRef(false);
  const [showSpecialRelease, setShowSpecialRelease] = useState(false);
  const [hasMonitoringService, setHasMonitoringService] = useState(false);

  useEffect(() => {
    const loadOrder = async () => {
      try {
        const email = decodeToken(localStorage.getItem("token") || "");
        const candidate = await getCandidateByEmail(email);
        setCandidate(candidate);
        const order = await getOrderByID(orderID);
        setOrder(order);
        const orderForm = await getOrderFormByID(orderFormID);
        setOrderForm(orderForm);
        const criminalOrder = await getCriminalOrderByOrderID(orderID);
        setCriminalOrder(criminalOrder);
        const clientResponse = await getSimpleClient(order.clientID);
        setClient(clientResponse);

        const yearsToGoBack = criminalOrder?.years || 0;
        const today = new Date();
        const pastDate = new Date();
        pastDate.setFullYear(today.getFullYear() - yearsToGoBack);
        setStartDate(pastDate);

        const orderServicesResponse = await getOrderServicesByOrderID(
          order.orderID,
        );
        if (orderServicesResponse) {
          setHasEmploymentService(
            orderServicesResponse?.some(
              (orderService: OrderService) =>
                orderService.serviceID === 30 ||
                orderService.serviceID === 31 ||
                orderService.serviceID === 32,
            ),
          );

          const hasOne = orderServicesResponse?.some(
            (orderService: OrderService) => orderService.serviceID === 9,
          );
          const hasMultiple = orderServicesResponse?.some(
            (orderService: OrderService) => orderService.serviceID === 23,
          );

          setHasOneEducationService(hasOne);
          setHasMultipleEducationService(hasMultiple);

          if ((hasOne || hasMultiple) && !hasRun.current) {
            handleAddAnotherEducation();
            hasRun.current = true;
          }

          const showSpecial = orderServicesResponse?.some(
            (orderService: OrderService) =>
              orderService.serviceID === 5 || orderService.serviceID === 31,
          );
          setShowSpecialRelease(showSpecial);

          const monitoring = orderServicesResponse?.some(
            (orderService: OrderService) => orderService.serviceID === 36,
          );
          setHasMonitoringService(monitoring);

          if (orderForm) {
            const draft = await loadCriminalAuthDraft(
              order?.orderID,
              orderForm?.orderFormID,
            );

            if (draft) {
              setPersonalInformation(draft.personalInformation);
              setConvictedOfCrimeList(draft.convictedOfCrimeList || []);
              setConvictedOfCrimeSelection(draft.convictedOfCrimeSelection);
              setLegalNamesList(draft.legalNamesList || []);
              setAdditionalNameSelection(draft.additionalNameSelection);
              setResidentialAddressList(draft.residentialAddressList || []);
              setEmploymentAddressList(draft.employmentAddressList || []);
              setAuthorization(draft.authorization);
              setEducationList(draft.educationList || []);
              setUnemployed(draft.unemployed || false);
            }
          }

          setPersonalInformation((prev) => ({
            ...prev,
            email: candidate.candidateEmail,
          }));

          if (anyCompletedForms) {
            setPersonalInformation((prev) => ({
              ...prev,
              firstName: order.candidateFirstName ?? "",
              middleName: order.candidateMI ?? "",
              lastName: order.candidateLastName ?? "",
              phone: order.candidatePhone ?? "",
              dateOfBirth: order.candidateDOB ?? new Date(),
              socialSecurityNumber: order.candidateSSN ?? "",
              canadianSocialSecurityNumber: order.canadianSIN ?? "",
              address: order.candidateAddress ?? "",
              city: order.candidateCity ?? "",
              state: order.candidateState ?? "",
              zipCode: order.candidateZipCode ?? "",
            }));
          }
        }
      } catch (error) {
        console.error("Error fetching candidate, order or orderForm:", error);
      } finally {
        setLoading(false);
      }
    };

    loadOrder();
  }, [orderFormID, orderID, decodeToken, anyCompletedForms]);

  // Draft Pieces
  const buildDraftObject = (): CriminalAuthDraft => {
    return {
      personalInformation,
      convictedOfCrimeList,
      convictedOfCrimeSelection,
      legalNamesList,
      additionalNameSelection,
      residentialAddressList,
      employmentAddressList,
      authorization,
      educationList,
      unemployed,
    };
  };

  const handleSaveDraft = async () => {
    try {
      setLoadingSpinner(true);
      if (!order || !orderForm) return;

      const draft: CriminalAuthDraft = buildDraftObject();

      const response = await saveDraftCriminalAuth(
        order?.orderID,
        orderForm?.orderFormID,
        draft,
      );

      if (response) {
        navigate("/candidate/needs-review", {
          state: { successMessage: "Draft saved successfully." },
        });
      }
    } catch (error) {
      console.error("Error saving draft:", error);
      alert("An error occurred while saving your draft.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
    field: string,
    index?: number,
    type?: string,
  ) => {
    let value: string | boolean =
      e.target.type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.value;

    if (typeof value === "string" && /^\d{4}-\d{2}$/.test(value)) {
      value = `${value}-01`;
    }

    if (field === "convictedOfCrime" && e.target.type === "radio") {
      value = e.target.value === "true";
    }

    setValidationErrors((prevErrors) => ({ ...prevErrors, [field]: "" }));

    if (index !== undefined) {
      if (convictedOfCrimeList[index] && field in convictedOfCrimeList[index]) {
        setValidationErrors((prevErrors) => {
          const updatedErrors = { ...prevErrors };
          delete updatedErrors[`${field}-${index}`];
          return updatedErrors;
        });
        setConvictedOfCrimeList((prevList) => {
          const updatedList = [...prevList];
          updatedList[index] = { ...updatedList[index], [field]: value };
          return updatedList;
        });
      } else if (legalNamesList[index] && field in legalNamesList[index]) {
        setValidationErrors((prevErrors) => {
          const updatedErrors = { ...prevErrors };
          delete updatedErrors[`${field}-${index}`];
          return updatedErrors;
        });
        setLegalNamesList((prevList) => {
          const updatedList = [...prevList];
          updatedList[index] = { ...updatedList[index], [field]: value };
          return updatedList;
        });
      } else if (
        educationList[index] &&
        field in educationList[index] &&
        type !== "Residential" &&
        type !== "Employment"
      ) {
        setValidationErrors((prevErrors) => {
          const updatedErrors = { ...prevErrors };
          delete updatedErrors.educationalInstitution;
          delete updatedErrors[`${field}-${index}`];
          return updatedErrors;
        });
        setEducationList((prevList) => {
          const updatedList = [...prevList];
          updatedList[index] = { ...updatedList[index], [field]: value };
          return updatedList;
        });
      } else if (
        (type === "Residential" &&
          residentialAddressList[index] &&
          field in residentialAddressList[index]) ||
        (type === "Employment" &&
          employmentAddressList[index] &&
          field in employmentAddressList[index])
      ) {
        setValidationErrors((prevErrors) => {
          const updatedErrors = { ...prevErrors };
          delete updatedErrors[`specialReleaseForm`];
          delete updatedErrors[`${field}-${type}-${index}`];
          return updatedErrors;
        });
        if (type === "Residential") {
          setResidentialAddressList((prevList) => {
            const updatedList = [...prevList];
            updatedList[index] = { ...updatedList[index], [field]: value };
            return updatedList;
          });
        } else if (type === "Employment") {
          setEmploymentAddressList((prevList) => {
            const updatedList = [...prevList];
            updatedList[index] = { ...updatedList[index], [field]: value };
            return updatedList;
          });
        }
      }
    }

    if (field === "unemployed") {
      const isUnemployed = value as boolean;
      setUnemployed(isUnemployed);

      if (isUnemployed) {
        employmentAddressList.forEach((_, index) =>
          handleDeleteEmploymentAddress(index),
        );
      }
    } else if (field in personalInformation) {
      setPersonalInformation((prev) => ({
        ...prev,
        [field]: value,
      }));
    } else if (field in authorization) {
      setAuthorization((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleYesNoChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    kind: string,
  ) => {
    const value = e.target.value === "true";

    if (kind === "crime") {
      setConvictedOfCrimeSelection(value);
      handleAddAnotherCrime();

      if (!value) {
        setConvictedOfCrimeList([]);
      }

      setValidationErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        delete updatedErrors.convictedOfCrimeSelection;
        return updatedErrors;
      });
    } else if (kind === "name") {
      setAdditionalNameSelection(value);
      handleAddAnotherLegalName();

      if (!value) {
        setLegalNamesList([]);
      }

      setValidationErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        delete updatedErrors.additionalNameSelection;
        return updatedErrors;
      });
    }
  };

  const handleAddAnotherCrime = () => {
    setConvictedOfCrimeList((prevList) => [
      ...prevList,
      {
        convictedOfCrime: true,
        ccState: "",
        ccCounty: "",
        ccYear: "",
        comments: "",
      },
    ]);

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      delete updatedErrors.convictedOfCrimeSelection;
      return updatedErrors;
    });
  };

  const handleAddAnotherLegalName = () => {
    setLegalNamesList((prevList) => [
      ...prevList,
      {
        lnFirstName: "",
        lnLastName: "",
        lnMiddleName: "",
        lnFromDate: new Date(),
        lnToDate: new Date(),
        confidential: false,
      },
    ]);

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      delete updatedErrors.additionalNameSelection;
      return updatedErrors;
    });
  };

  const handleAddAnotherEducation = () => {
    setEducationList((prevList) => [
      ...prevList,
      {
        educationName: "",
        highestDegree: "",
        degreeDate: new Date(),
        city: "",
        state: "",
        country: "",
      },
    ]);

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      delete updatedErrors.educationalInstitution;
      return updatedErrors;
    });
  };

  const handleAddAnotherResidentialAddress = () => {
    setResidentialAddressList((prevList) => {
      const lastAddress =
        prevList.length > 0 ? prevList[prevList.length - 1] : null;

      return [
        ...prevList,
        {
          address: "",
          zipCode: "",
          city: "",
          state: "",
          county: "",
          country: "",
          fromDate: lastAddress ? new Date(lastAddress.fromDate) : new Date(),
          toDate: lastAddress ? new Date(lastAddress.fromDate) : new Date(),
        },
      ];
    });

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      delete updatedErrors.residentialAddressLength;
      delete updatedErrors.addressHistory;
      return updatedErrors;
    });
  };

  const handleAddAnotherEmploymentAddress = () => {
    setEmploymentAddressList((prevList) => {
      const lastAddress =
        prevList.length > 0 ? prevList[prevList.length - 1] : null;

      return [
        ...prevList,
        {
          address: "",
          zipCode: "",
          city: "",
          state: "",
          county: "",
          country: "",
          fromDate: lastAddress ? new Date(lastAddress.fromDate) : new Date(),
          toDate: lastAddress ? new Date(lastAddress.fromDate) : new Date(),
        },
      ];
    });

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      delete updatedErrors.unemployed;
      return updatedErrors;
    });
  };

  const handleDeleteConviction = (index: number) => {
    setConvictedOfCrimeList((prevList) =>
      prevList.filter((_, i) => i !== index),
    );

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      Object.keys(updatedErrors).forEach((key) => {
        if (
          key.startsWith(`ccState-${index}`) ||
          key.startsWith(`ccCounty-${index}`) ||
          key.startsWith(`ccYear-${index}`) ||
          key.startsWith(`comments-${index}`)
        ) {
          delete updatedErrors[key];
        }
      });
      return updatedErrors;
    });
  };

  const handleDeleteLegalName = (index: number) => {
    setLegalNamesList((prevList) => prevList.filter((_, i) => i !== index));

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      Object.keys(updatedErrors).forEach((key) => {
        if (
          key.startsWith(`lnFirstName-${index}`) ||
          key.startsWith(`lnLastName-${index}`) ||
          key.startsWith(`lnFromDate-${index}`) ||
          key.startsWith(`lnToDate-${index}`)
        ) {
          delete updatedErrors[key];
        }
      });
      return updatedErrors;
    });
  };

  const handleDeleteEducation = (index: number) => {
    setEducationList((prevList) => prevList.filter((_, i) => i !== index));

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      Object.keys(updatedErrors).forEach((key) => {
        if (
          key.startsWith(`educationName-${index}`) ||
          key.startsWith(`highestDegree-${index}`) ||
          key.startsWith(`degreeDate-${index}`)
        ) {
          delete updatedErrors[key];
        }
      });
      return updatedErrors;
    });
  };

  const handleDeleteResidentialAddress = (index: number) => {
    setResidentialAddressList((prevList) =>
      prevList.filter((_, i) => i !== index),
    );

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      Object.keys(updatedErrors).forEach((key) => {
        if (
          key.startsWith(`address-Residential-${index}`) ||
          key.startsWith(`zipCode-Residential-${index}`) ||
          key.startsWith(`city-Residential-${index}`) ||
          key.startsWith(`state-Residential-${index}`) ||
          key.startsWith(`county-Residential-${index}`) ||
          key.startsWith(`fromDate-Residential-${index}`) ||
          key.startsWith(`toDate-Residential-${index}`)
        ) {
          delete updatedErrors[key];
        }
      });
      return updatedErrors;
    });
  };

  const handleDeleteEmploymentAddress = (index: number) => {
    setEmploymentAddressList((prevList) =>
      prevList.filter((_, i) => i !== index),
    );

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      Object.keys(updatedErrors).forEach((key) => {
        if (
          key.startsWith(`address-Employment-${index}`) ||
          key.startsWith(`zipCode-Employment-${index}`) ||
          key.startsWith(`city-Employment-${index}`) ||
          key.startsWith(`state-Employment-${index}`) ||
          key.startsWith(`county-Employment-${index}`) ||
          key.startsWith(`fromDate-Employment-${index}`) ||
          key.startsWith(`toDate-Employment-${index}`)
        ) {
          delete updatedErrors[key];
        }
      });
      return updatedErrors;
    });
  };

  const validateFields = async () => {
    const errors: { [key: string]: string } = {};

    // Personal Information Validations
    if (!personalInformation?.firstName) {
      errors.firstName = "First name is required.";
    }
    if (!personalInformation?.lastName) {
      errors.lastName = "Last name is required.";
    }
    if (!personalInformation?.email) {
      errors.email = "Email address is required.";
    } else if (!isValidEmail(personalInformation.email)) {
      errors.email = "Please enter a valid email address.";
    }
    if (!personalInformation?.phone) {
      errors.phone = "Phone number is required.";
    } else if (!isValidPhone(personalInformation.phone)) {
      errors.phone = "Please enter a valid phone number.";
    }
    if (!personalInformation?.race) {
      errors.race = "Race is required.";
    }
    if (!personalInformation?.sex) {
      errors.sex = "Sex is required.";
    }
    if (
      !personalInformation?.socialSecurityNumber &&
      !personalInformation.canadianSocialSecurityNumber
    ) {
      errors.socialSecurityNumber = "A SSN OR SIN is required.";
    } else {
      //check the ssn or sin for validation errors
      if (
        personalInformation.socialSecurityNumber &&
        !isValidSSN(personalInformation.socialSecurityNumber)
      ) {
        errors.socialSecurityNumber = "Enter a valid SSN";
      }

      if (
        personalInformation.canadianSocialSecurityNumber &&
        !isValidSIN(personalInformation.canadianSocialSecurityNumber)
      ) {
        errors.canadianSocialSecurityNumber = "Enter a valid SIN";
      }
    }
    if (!isValidDate(personalInformation?.dateOfBirth)) {
      errors.dateOfBirth = "DOB is required and must be valid.";
    } else if (!isValidBirthday(personalInformation.dateOfBirth)) {
      errors.dateOfBirth = "Please check your birth date";
    }

    // Convicted of Crime Validations
    if (convictedOfCrimeSelection === null) {
      errors.convictedOfCrimeSelection = "Please select Yes or No.";
    } else if (
      convictedOfCrimeSelection === true &&
      convictedOfCrimeList.length === 0
    ) {
      errors.convictedOfCrimeSelection =
        "You must enter at least one criminal record.";
    }

    convictedOfCrimeList.forEach((conviction, index) => {
      if (convictedOfCrimeSelection === true) {
        if (!conviction.ccState)
          errors[`ccState-${index}`] = "State is required.";
        if (!conviction.ccCounty) {
          errors[`ccCounty-${index}`] = "County is required.";
        } else if (!isValidCounty(conviction.ccCounty)) {
          errors[`ccCounty-${index}`] = "Please enter a county not a country.";
        }
        if (!conviction.ccYear || !/^\d{4}$/.test(conviction.ccYear)) {
          errors[`ccYear-${index}`] = "A valid year is required.";
        }
        if (!conviction.comments)
          errors[`comments-${index}`] = "Comments are required.";
      }
    });

    // Legal Names Validations
    if (additionalNameSelection === null) {
      errors.additionalNameSelection = "Please select Yes or No.";
    } else if (
      additionalNameSelection === true &&
      legalNamesList.length === 0
    ) {
      errors.additionalNameSelection =
        "You must enter at least one legal name.";
    }

    legalNamesList.forEach((name, index) => {
      if (additionalNameSelection === true) {
        if (!name.lnFirstName)
          errors[`lnFirstName-${index}`] = "First name is required.";
        if (!name.lnLastName)
          errors[`lnLastName-${index}`] = "Last name is required.";
        if (!isValidDate(name.lnFromDate)) {
          errors[`lnFromDate-${index}`] =
            "From date is required and must be valid.";
        }
        if (!isValidDate(name.lnToDate)) {
          errors[`lnToDate-${index}`] =
            "To date is required and must be valid.";
        }
      }
    });

    // Education Validation
    if (
      (hasOneEducationService === true ||
        hasMultipleEducationService === true) &&
      educationList.length === 0
    ) {
      errors.educationalInstitution =
        "You must enter at least one educational institution.";
    }

    educationList.forEach((education, index) => {
      if (!education.educationName)
        errors[`educationName-${index}`] = "Institution name is required.";
      if (!education.highestDegree)
        errors[`highestDegree-${index}`] = "Highest Degree is required.";
      if (!isValidDate(education.degreeDate)) {
        errors[`degreeDate-${index}`] =
          "Degree date is required and must be valid.";
      }
      if (!education.city) {
        errors[`city-${index}`] = "Degree City is required.";
      }
      if (!education.state) {
        errors[`state-${index}`] = "Degree State is required.";
      }
    });

    // Residential Address Validations
    if (residentialAddressList.length === 0) {
      errors.residentialAddressLength =
        "You must enter at least one residential address.";
    }

    residentialAddressList.forEach((address, index) => {
      if (!address.address)
        errors[`address-Residential-${index}`] =
          "Residential address is required.";
      if (!address.zipCode)
        errors[`zipCode-Residential-${index}`] = "ZipCode is required.";
      else if (!isValidZip(address.zipCode))
        errors[`zipCode-Residential-${index}`] =
          "Please enter a valid zip code.";
      if (!address.city)
        errors[`city-Residential-${index}`] = "City is required.";
      if (!address.state)
        errors[`state-Residential-${index}`] = "State is required.";
      if (
        (address.state === "GA" || address.state === "NV") &&
        file == null &&
        showSpecialRelease
      ) {
        errors.specialReleaseForm =
          "Please complete and upload the special release form.";
      }
      if (!address.county) {
        errors[`county-Residential-${index}`] = "County is required.";
      } else if (!isValidCounty(address.county)) {
        errors[`county-Residential-${index}`] =
          "Please enter a county not a country.";
      }
      if (!isValidDate(address.fromDate)) {
        errors[`fromDate-Residential-${index}`] =
          "From date is required and must be valid.";
      }
      if (!isValidDate(address.toDate)) {
        errors[`toDate-Residential-${index}`] =
          "To date is required and must be valid.";
      }
    });

    // Employment Address Validations
    if (hasEmploymentService) {
      employmentAddressList.forEach((address, index) => {
        if (!address.address)
          errors[`address-Employment-${index}`] =
            "Employment address is required.";
        if (!address.zipCode)
          errors[`zipCode-Employment-${index}`] = "ZipCode is required.";
        else if (!isValidZip(address.zipCode))
          errors[`zipCode-Employment-${index}`] =
            "Please enter a valid zip code.";
        if (!address.city)
          errors[`city-Employment-${index}`] = "City is required.";
        if (!address.state)
          errors[`state-Employment-${index}`] = "State is required.";
        if (
          (address.state === "GA" || address.state === "NV") &&
          file == null &&
          showSpecialRelease
        ) {
          errors.specialReleaseForm =
            "Please complete and upload the special release form.";
        }
        if (!address.county) {
          errors[`county-Employment-${index}`] = "County is required.";
        } else if (!isValidCounty(address.county)) {
          errors[`county-Employment-${index}`] =
            "Please enter a county not a country.";
        }
        if (!isValidDate(address.fromDate)) {
          errors[`fromDate-Employment-${index}`] =
            "From date is required and must be valid.";
        }
        if (!isValidDate(address.toDate)) {
          errors[`toDate-Employment-${index}`] =
            "To date is required and must be valid.";
        }
      });

      if (employmentAddressList.length === 0 && unemployed === false) {
        errors.unemployed =
          "You must either add an employment address or attest that you were unemployed. ";
      }
    }

    // Authorization Validation
    if (!authorization.checked) {
      errors.checked = "You must agree before continuing.";
    }
    if (!isValidDate(authorization.dated)) {
      errors.dated = "Date is required and must be valid.";
    }
    if (!authorization.fullName) {
      errors.fullName = "Full name is required.";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormUpload = async () => {
    if (!orderFormID) {
      console.error("Order Form ID is missing");
      return;
    }

    try {
      setLoadingSpinner(true);
      const fileInput = document.createElement("input");
      fileInput.type = "file";
      fileInput.accept = ".pdf";
      fileInput.onchange = async (event: any) => {
        const file = event.target.files[0];
        if (!file) return;
        setFile(file);
        setFileUploaded(true);
        setSuccessMessage("Special Release form uploaded successfully");
        setValidationErrors((prevErrors) => {
          const updatedErrors = { ...prevErrors };
          delete updatedErrors[`specialReleaseForm`];
          return updatedErrors;
        });
      };
      fileInput.click();
    } catch (error) {
      setFileUploaded(false);
      console.error("Error handling file upload:", error);
    } finally {
      setLoadingSpinner(false);
    }
  };

  const handleSave = async () => {
    setLoadingSpinner(true);
    const isValid = await validateFields();
    if (!isValid) {
      console.log("Validation failed. Please fix the errors.");
      setLoadingSpinner(false);
      const firstErrorElement = document.querySelector(".invalid-feedback");
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
      return;
    }

    if (criminalOrder) {
      const addressErrors = validateAddressHistory(
        residentialAddressList,
        criminalOrder.years,
      );

      if (addressErrors.length > 0) {
        setValidationErrors((prevErrors) => ({
          ...prevErrors,
          addressHistory: addressErrors.join(" "),
        }));
        setTimeout(() => {
          const firstErrorElement = document.querySelector(".invalid-feedback");
          if (firstErrorElement) {
            firstErrorElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }, 100);

        setLoadingSpinner(false);
        return;
      }

      setValidationErrors((prevErrors) => {
        const { addressHistory, ...remainingErrors } = prevErrors;
        return remainingErrors;
      });

      try {
        if (orderForm?.orderFormID) {
          const result = await saveCriminalRecordsAuthForm(
            criminalOrder.criminalOrderID,
            orderForm?.orderFormID,
            personalInformation,
            convictedOfCrimeList,
            legalNamesList,
            residentialAddressList,
            employmentAddressList,
            authorization,
            criminalOrder.years,
            client?.diocese ? client.diocese : "",
            client?.abbreviation ? client?.abbreviation : "",
            hasEmploymentService,
            hasOneEducationService || hasMultipleEducationService
              ? true
              : false,
            file ? file : undefined,
            educationList,
            hasOneEducationService,
            hasMonitoringService,
          );

          if (file && fileUploaded) {
            await uploadSpecialReleaseForm(file, orderFormID);
          }

          if (result) {
            try {
              if (order && orderFormID)
                await deleteDraft(
                  order.orderID,
                  orderForm.orderFormID,
                  "criminalAuth",
                );
            } catch (error) {
              console.warn(
                "Draft deletion failed or was already deleted:",
                error,
              );
            }
            navigate("/candidate/needs-review", {
              state: { successMessage: "Form successfully submitted." },
            });
          }
        }
      } catch (error) {
        console.error("Error saving criminal records authorization:", error);
        alert("An error occurred while saving the form. Please try again.");
      } finally {
        setLoadingSpinner(false);
      }
    }
  };

  return (
    <div className="content-container">
      <div className="form-header-container">
        <h1>
          {order?.candidateFirstName} {order?.candidateLastName}
        </h1>
        <div className="page-items-container"></div>
      </div>

      {loading ? (
        <div className="loading-divider">
          <div className="loading-bar"></div>
        </div>
      ) : (
        <>
          <div className="divider"></div>
          <div className="text-form-header">
            AUTHORIZATION TO CONDUCT CRIMINAL RECORDS CHECK
          </div>
          <div>
            The items of personal information requested below are needed to
            process your criminal records check. This information is intended
            solely for that purpose and will not be used in a discriminatory
            manner by the parties noted below in the making of appropriate
            business decisions.
          </div>
          <br></br>

          {/* Personal Information */}
          <PersonalInformationForm
            personalInformation={personalInformation}
            criminalOrderYears={criminalOrder?.years ? criminalOrder.years : 7}
            validationErrors={validationErrors}
            onInputChange={handleInputChange}
            onFormUpload={handleFormUpload}
            fileUploaded={fileUploaded}
            employment={hasEmploymentService}
            suran={false}
            showSpecialRelease={showSpecialRelease}
          />

          {/* Convicted of Crime */}
          <div className="data-container">
            <div className="text-body" style={{ flex: "0 0 100%" }}>
              Have you ever been convicted of a crime (other than minor traffic
              offenses)?
            </div>
            <div className="field-long" style={{ flex: "0 0 10%" }}>
              <label className="field-label">Yes:</label>
              <input
                className="form-check-input"
                type="radio"
                name="convictedOfCrime"
                value="true"
                checked={convictedOfCrimeSelection === true}
                onChange={(e) => handleYesNoChange(e, "crime")}
              />
            </div>
            <div className="field-long" style={{ flex: "0 0 10%" }}>
              <label className="field-label">No:</label>
              <input
                className="form-check-input"
                type="radio"
                name="convictedOfCrime"
                value="false"
                checked={convictedOfCrimeSelection === false}
                onChange={(e) => handleYesNoChange(e, "crime")}
              />
            </div>
            {validationErrors.convictedOfCrimeSelection && (
              <div className="invalid-feedback" style={{ display: "block" }}>
                {validationErrors.convictedOfCrimeSelection}
              </div>
            )}
            <div style={{ flex: "0 0 70%" }}></div>

            {convictedOfCrimeSelection && (
              <div style={{ flex: "0 0 100%" }}>
                {convictedOfCrimeList.map((convictedOfCrime, index) => (
                  <ConvictedOfCrimeForm
                    key={index}
                    convictedOfCrime={convictedOfCrime}
                    validationErrors={validationErrors}
                    onInputChange={handleInputChange}
                    onDelete={() => handleDeleteConviction(index)}
                    index={index}
                  />
                ))}
                <div className="btn-center" style={{ flex: "0 0 100%" }}>
                  <button
                    className="oxbtn btn-purple"
                    onClick={() => handleAddAnotherCrime()}
                  >
                    Add An Entry
                    <i className="bi bi-plus-lg btn-icon"></i>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Legal Names */}
          <div className="data-container">
            <div className="text-body" style={{ flex: "0 0 100%" }}>
              Have you ever used other legal names, including maiden name?
            </div>
            <div className="field-long" style={{ flex: "0 0 10%" }}>
              <label className="field-label">Yes:</label>
              <input
                className="form-check-input"
                type="radio"
                name="additionalName"
                value="true"
                checked={additionalNameSelection === true}
                onChange={(e) => handleYesNoChange(e, "name")}
              />
            </div>
            <div className="field-long" style={{ flex: "0 0 10%" }}>
              <label className="field-label">No:</label>
              <input
                className="form-check-input"
                type="radio"
                name="additionalName"
                value="false"
                checked={additionalNameSelection === false}
                onChange={(e) => handleYesNoChange(e, "name")}
              />
            </div>
            {validationErrors.additionalNameSelection && (
              <div className="invalid-feedback" style={{ display: "block" }}>
                {validationErrors.additionalNameSelection}
              </div>
            )}
            <div style={{ flex: "0 0 70%" }}></div>

            {additionalNameSelection && (
              <div style={{ flex: "0 0 100%" }}>
                <div className="text-body" style={{ flex: "0 0 100%" }}>
                  Other legal names you have used, including maiden name and the
                  year(s) your name(s) changed:
                </div>
                <br></br>
                {legalNamesList.map((legalName, index) => (
                  <LegalNamesForm
                    key={index}
                    legalName={legalName}
                    validationErrors={validationErrors}
                    onInputChange={handleInputChange}
                    onDelete={() => handleDeleteLegalName(index)}
                    index={index}
                  />
                ))}
                <br></br>
                <div className="btn-center" style={{ flex: "0 0 100%" }}>
                  <button
                    className="oxbtn btn-purple"
                    onClick={() => handleAddAnotherLegalName()}
                  >
                    Add A Previous Legal Name
                    <i className="bi bi-plus-lg btn-icon"></i>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Education */}
          {(hasOneEducationService || hasMultipleEducationService) && (
            <div className="data-container">
              <div className="text-body" style={{ flex: "0 0 100%" }}>
                <div className="text-container-header">
                  {hasOneEducationService
                    ? "Highest Degree Attained"
                    : "Degrees Attained"}{" "}
                </div>
              </div>
              <div className="text-body" style={{ flex: "0 0 100%" }}>
                {hasOneEducationService
                  ? "Please list the highest degree attained you have received:"
                  : "Please list all degrees you have received:"}{" "}
              </div>
              {validationErrors.educationalInstitution && (
                <div className="invalid-feedback" style={{ display: "block" }}>
                  {validationErrors.educationalInstitution}
                </div>
              )}

              <div style={{ flex: "0 0 100%" }}>
                <br></br>
                {educationList.map((education, index) => (
                  <EducationForm
                    key={index}
                    education={education}
                    validationErrors={validationErrors}
                    onInputChange={handleInputChange}
                    onDelete={() => handleDeleteEducation(index)}
                    index={index}
                    oneEducation={hasOneEducationService}
                  />
                ))}
                <br></br>
                {hasMultipleEducationService && (
                  <div className="btn-center" style={{ flex: "0 0 100%" }}>
                    <button
                      className="oxbtn btn-purple"
                      onClick={() => handleAddAnotherEducation()}
                    >
                      Add Education
                      <i className="bi bi-plus-lg btn-icon"></i>
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Residential Addresses */}
          <div className="data-container">
            <div className="text-body" style={{ flex: "0 0 100%" }}>
              <div className="text-container-header">Residential Addresses</div>
              <br></br>
              Please list all of your residential addresses for the past{" "}
              {criminalOrder?.years} years, starting with your present address.
              (Include all addresses where you physically resided in the last{" "}
              {criminalOrder?.years} years including college and seasonal
              property addresses.)
              <ul>
                <li>
                  If any of your addresses are in Canada, please select "CANADA"
                  from the state drop-down list.
                </li>
                <li>
                  If any of your addresses are outside of the US and Canada,
                  please fill in your address but select "INT" from the state
                  drop-down list.
                </li>
                <li>
                  * You need to go back to <b>{formatDate(startDate, "us")}</b>{" "}
                  with your residential history. *
                </li>
              </ul>
            </div>
            {validationErrors.residentialAddressLength && (
              <div className="invalid-feedback" style={{ display: "block" }}>
                {validationErrors.residentialAddressLength}
              </div>
            )}
            {validationErrors.addressHistory && (
              <div className="invalid-feedback" style={{ display: "block" }}>
                {validationErrors.addressHistory}
              </div>
            )}
            {residentialAddressList.map((address, index) => (
              <AddressForm
                key={index}
                address={address}
                validationErrors={validationErrors}
                onInputChange={handleInputChange}
                onDelete={() => handleDeleteResidentialAddress(index)}
                index={index}
                type="Residential"
              />
            ))}
            <div className="btn-center" style={{ flex: "0 0 100%" }}>
              <button
                className="oxbtn btn-purple"
                onClick={handleAddAnotherResidentialAddress}
              >
                Add An Address
                <i className="bi bi-plus-lg btn-icon"></i>
              </button>
            </div>
          </div>

          {/* Employment Addresses */}
          {hasEmploymentService && (
            <div className="data-container">
              <div className="text-body" style={{ flex: "0 0 100%" }}>
                <div className="text-container-header">
                  Employment Addresses
                </div>
                <br></br>
                 Please list all of your employment addresses for the past{" "}
                {criminalOrder?.years} years. If you were not working during any
                point over the previous {criminalOrder?.years} years, simply
                skip over those periods of time.
                <ul>
                  <li>
                    If any of your addresses are in Canada please select
                    "CANADA" from the state drop-down list.
                  </li>
                  <li>
                    If any of your addresses are outside of the US and Canada,
                    please fill in your address but select "INT" from the state
                    drop-down list.
                  </li>
                  <li>
                    * You need to go back to{" "}
                    <b>{formatDate(startDate, "us")}</b> with your employment
                    history. *
                  </li>
                </ul>
              </div>
              <div className="field-long" style={{ flex: "0 0 100%" }}>
                <label className="field-label" style={{ textWrap: "wrap" }}>
                  I have been unemployed for this time period:
                </label>
                <div className="field-input" style={{ flex: "0 0 3%" }}>
                  <input
                    className={`form-check-input ${
                      validationErrors.unemployed ? "is-invalid" : ""
                    }`}
                    type="checkbox"
                    checked={unemployed}
                    onChange={(e) => handleInputChange(e, "unemployed")}
                  />
                </div>
                <div>
                  {validationErrors.unemployed && (
                    <div
                      className="invalid-feedback"
                      style={{ display: "block" }}
                    >
                      {validationErrors.unemployed}
                    </div>
                  )}
                </div>
              </div>
              {employmentAddressList.map((address, index) => (
                <AddressForm
                  key={index}
                  address={address}
                  validationErrors={validationErrors}
                  onInputChange={handleInputChange}
                  onDelete={() => handleDeleteEmploymentAddress(index)}
                  index={index}
                  type="Employment"
                />
              ))}
              <div className="btn-center" style={{ flex: "0 0 100%" }}>
                <button
                  className={`oxbtn btn-purple ${unemployed ? "disabled" : ""}`}
                  onClick={handleAddAnotherEmploymentAddress}
                  disabled={unemployed}
                >
                  Add An Address
                  <i className="bi bi-plus-lg btn-icon"></i>
                </button>
              </div>
            </div>
          )}

          {/* Authorization */}
          <div className="data-container">
            {hasMonitoringService ? (
              <div className="text-body" style={{ flex: "0 0 100%" }}>
                I authorize {client?.diocese} and/or Oxford Document Management
                and their agents to investigate my background in connection with
                my application for employment, appointment or a volunteer
                position. I understand that this may include investigative
                inquiries related to my criminal records (at the county, state,
                national, federal [district courts] and international levels),
                sex offender history, civil court records, SSN verification, and
                employment and education verification. I hereby release and hold
                harmless all persons, agencies or corporations furnishing such
                information from liability and responsibility. A photo
                static/fax/electronic copy of this document can be substituted
                for the original. This authorization shall remain on file and in
                effect and shall serve as an ongoing authorization for{" "}
                {client?.diocese} or its representatives to procure consumer
                reports at any time during my employment, appointment, or
                volunteer engagement.
              </div>
            ) : (
              <div className="text-body" style={{ flex: "0 0 100%" }}>
                I authorize {client?.diocese} and/or Oxford Document Management
                and their agents to investigate my background in connection with
                my application for employment, appointment or a volunteer
                position. I understand that this may include investigative
                inquiries related to my criminal records (at the county, state,
                national, federal [district courts] and international levels),
                sex offender history, civil court records, SSN verification, and
                employment and education verification. I hereby release and hold
                harmless all persons, agencies or corporations furnishing such
                information from liability and responsibility. A photo
                static/fax/electronic copy of this document can be substituted
                for the original. This authorization shall be valid for a period
                of one (1) year from the date of my signature.
              </div>
            )}

            <AuthorizationForm
              authText={
                "I attest that the answers I have given are true to the best of my knowledge and hereby consent to the release of the above-described records."
              }
              authorization={authorization}
              validationErrors={validationErrors}
              onInputChange={handleInputChange}
              type={""}
            />
          </div>

          <div className="action-buttons">
            <button
              className="oxbtn btn-grey"
              onClick={() => handleSaveDraft()}
            >
              Save and Return
            </button>

            <button className="oxbtn btn-green" onClick={() => handleSave()}>
              Submit
            </button>
          </div>

          {successMessage && (
            <div className="alert-message alert alert-success alert-dismissible fade show">
              {successMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccessMessage("")}
                aria-label="Close"
              ></button>
            </div>
          )}
        </>
      )}
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};
export default CriminalRecordsAuthForm;
